/**
 * Active-site selection.
 *
 * We persist only the chosen `siteId` (a number — not sensitive) and
 * scope the localStorage key by API host. Different networks
 * (ez-qr.menu vs ez-qr.online) maintain independent selections, so
 * switching domain at login doesn't carry a stale id across.
 *
 * `currentSite` is computed from the live session list — if the session
 * doesn't include the persisted id (logged out, removed from site, etc.)
 * it resolves to `null` and the view should kick the user back to /sites.
 */
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import type { Site } from '@/entities/site'
import { useSessionStore } from '@/app/stores/session.store'
import { env } from '@/app/config/env'
import { scopedStorage } from '@/shared/lib/scoped-storage'

// Key is env-scoped (`ezqr:<env>:current-site-id:<host>`) and host-scoped:
// env-scoping stops a different build's selection leaking in; host-scoping
// keeps independent selections per backend network. The host suffix is
// belt-and-braces now that the host itself is env-scoped, but harmless.
function storageKey(host: string | null): string {
  return `current-site-id:${host ?? env.apiHost}`
}

function readPersisted(host: string | null): number | null {
  const raw = scopedStorage.get(storageKey(host))
  if (raw === null) return null
  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : null
}

function writePersisted(host: string | null, id: number | null): void {
  scopedStorage.set(storageKey(host), id === null ? null : String(id))
}

export const useSiteStore = defineStore('site', () => {
  const session = useSessionStore()

  const currentSiteId = ref<number | null>(readPersisted(session.apiHost))

  // Keep localStorage in sync — same effect as Pinia persist plugin
  // but with host-scoped keys, which Pinia persist doesn't give us
  // without extra config.
  watch(
    () => [currentSiteId.value, session.apiHost] as const,
    ([id, host]) => writePersisted(host, id),
  )

  /**
   * Resolve the active site against the live session list. Returns null
   * if the persisted id is no longer in the user's allowed sites — the
   * view layer should treat that as "needs to pick a site" and route
   * to /sites.
   */
  const currentSite = computed<Site | null>(() => {
    if (currentSiteId.value === null) return null
    return session.sites.find((s) => s.id === currentSiteId.value) ?? null
  })

  /** True when the persisted id no longer matches any allowed site. */
  const isStale = computed(() => currentSiteId.value !== null && currentSite.value === null)

  function selectSite(id: number): void {
    // Only allow ids that the session actually permits — defense in depth.
    // The server is the real authority, but rejecting here avoids a
    // pointless round-trip and a confusing redirect loop.
    const allowed = session.sites.some((s) => s.id === id)
    if (!allowed) return
    currentSiteId.value = id
  }

  function clearSelection(): void {
    currentSiteId.value = null
  }

  /**
   * Auto-select when a single site is available and nothing is selected
   * (or the selection is stale). Idempotent and cheap to call from
   * navigation guards.
   */
  function autoSelectIfSingle(): number | null {
    if (currentSite.value !== null) return currentSite.value.id
    if (session.sites.length === 1) {
      const only = session.sites[0]
      if (only) {
        currentSiteId.value = only.id
        return only.id
      }
    }
    return null
  }

  return {
    // state
    currentSiteId,
    // getters
    currentSite,
    isStale,
    // actions
    selectSite,
    clearSelection,
    autoSelectIfSingle,
  }
})
